/**
 * Lifeline 16-911 - Continuous G2 Squircle Geometry Engine
 * Implements the canonical continuous-g2-v1 geometry specification with two cubic Béziers per corner.
 * Ensures genuine G2 continuous curvature across all modern browsers, with zero rigid fixed-pixel rounding.
 */
(function(root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.LifelineSquircle = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
  'use strict';

  // Canonical continuous-g2-v1 constants (two cubics per corner, G2 at straight edges)
  const A = 0.43416567446713084;
  const B = 0.681792830507429;
  const M = 0.8408964152537145;
  const Q = 1 - M;

  /**
   * Generates an SVG path string for a rectangle with G2 continuous squircle corners.
   * @param {number} width - Element width
   * @param {number} height - Element height
   * @param {number} radius - Corner radius
   * @returns {string} SVG path data string
   */
  function continuousPath(width, height, radius) {
    const w = Math.max(0, width);
    const h = Math.max(0, height);
    const r = Math.min(Number.isFinite(radius) ? Math.max(0, radius) : 0, w / 2, h / 2);

    if (r === 0) {
      return `M 0 0 L ${w} 0 L ${w} ${h} L 0 ${h} Z`;
    }

    const commands = [];
    const point = p => p.map(n => Number(n.toFixed(3))).join(' ');
    const corner = map => {
      commands.push('C ' + [map(A, 0), map(B, 0), map(M, Q)].map(point).join(' '));
      commands.push('C ' + [map(1, 2 * Q), map(1, 1 - A), map(1, 1)].map(point).join(' '));
    };

    commands.push(`M ${r} 0`, `L ${w - r} 0`);
    corner((u, v) => [w - r + u * r, v * r]);
    commands.push(`L ${w} ${h - r}`);
    corner((u, v) => [w - v * r, h - r + u * r]);
    commands.push(`L ${r} ${h}`);
    corner((u, v) => [r - u * r, h - v * r]);
    commands.push(`L 0 ${r}`);
    corner((u, v) => [v * r, r - u * r]);

    return commands.join(' ') + ' Z';
  }

  /**
   * Generates a normalized [0, 1] SVG path string for clipPathUnits="objectBoundingBox".
   * Ideal for 1:1 square/circular icons and tags without per-frame recalculation.
   * @param {number} ratio - Normalized corner ratio (r / minDimension, max 0.5)
   * @returns {string} SVG path data string in [0, 1] range
   */
  function normalizedSquirclePath(ratio) {
    const r = Math.min(Math.max(0, Number.isFinite(ratio) ? ratio : 0.25), 0.5);
    const commands = [];
    const point = p => p.map(n => Number(n.toFixed(5))).join(' ');
    const corner = map => {
      commands.push('C ' + [map(A, 0), map(B, 0), map(M, Q)].map(point).join(' '));
      commands.push('C ' + [map(1, 2 * Q), map(1, 1 - A), map(1, 1)].map(point).join(' '));
    };

    commands.push(`M ${r.toFixed(5)} 0`, `L ${(1 - r).toFixed(5)} 0`);
    corner((u, v) => [1 - r + u * r, v * r]);
    commands.push(`L 1 ${(1 - r).toFixed(5)}`);
    corner((u, v) => [1 - v * r, 1 - r + u * r]);
    commands.push(`L ${r.toFixed(5)} 1`);
    corner((u, v) => [r - u * r, 1 - v * r]);
    commands.push(`L 0 ${r.toFixed(5)}`);
    corner((u, v) => [v * r, r - u * r]);

    return commands.join(' ') + ' Z';
  }

  // Browser-only DOM observer & clipPath injection
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    let svgDefs = null;

    function getDefs() {
      if (svgDefs && svgDefs.parentNode) return svgDefs;
      let svg = document.getElementById('lifeline-squircle-svg');
      if (!svg) {
        svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.id = 'lifeline-squircle-svg';
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;z-index:-1;';
        svgDefs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        svgDefs.id = 'lifeline-squircle-defs';
        svg.appendChild(svgDefs);
        (document.body || document.documentElement).appendChild(svg);
      } else {
        svgDefs = svg.querySelector('defs');
      }
      return svgDefs;
    }

    let clipCounter = 0;
    const elementCache = new WeakMap();

    function applyElementSquircle(el) {
      if (!el || !el.isConnected) return;
      // Do not apply SVG clipPath to interactive buttons and elevated menu surfaces; they use native CSS corner-shape & border-radius to preserve outer box-shadows and rim highlights
      if (el.matches && el.matches('button, a.button, .button, .floating-widget__btn, .header__menu-item, .header__icon, .list-menu--disclosure, .header__submenu, .mega-menu__content, .menu-drawer')) return;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w < 2 || h < 2) return;

      const rawRadius = el.dataset.squircle || (window.getComputedStyle ? window.getComputedStyle(el).borderTopLeftRadius : '16');
      const r = Math.max(0, parseFloat(rawRadius) || 16);

      let data = elementCache.get(el);
      if (!data) {
        clipCounter++;
        const id = 'll-squircle-clip-' + clipCounter;
        const clip = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
        clip.id = id;
        clip.setAttribute('clipPathUnits', 'userSpaceOnUse');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        clip.appendChild(path);
        getDefs().appendChild(clip);

        data = { id, path, w: 0, h: 0, r: 0 };
        elementCache.set(el, data);

        el.style.clipPath = `url(#${id})`;
        el.style.webkitClipPath = `url(#${id})`;
        el.setAttribute('data-squircle-mode', 'continuous-g2-v1');
      }

      if (data.w !== w || data.h !== h || data.r !== r) {
        data.w = w;
        data.h = h;
        data.r = r;
        data.path.setAttribute('d', continuousPath(w, h, r));
      }
    }

    let observer = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(entries => {
        for (let i = 0; i < entries.length; i++) {
          applyElementSquircle(entries[i].target);
        }
      });
    }

    function scanAndApply() {
      const targets = document.querySelectorAll(
        '[data-squircle-clip], .squircle-g2'
      );
      targets.forEach(el => {
        applyElementSquircle(el);
        if (observer) observer.observe(el);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scanAndApply, { once: true });
    } else {
      scanAndApply();
    }

    // Refresh on layout shifts, menu openings, or window resize
    window.addEventListener('resize', scanAndApply, { passive: true });
    document.addEventListener('click', event => {
      if (event.target.closest('header-menu details, .mega-menu, .floating-widget')) {
        setTimeout(scanAndApply, 50);
        setTimeout(scanAndApply, 250);
      }
    }, { passive: true });
  }

  return {
    version: 'continuous-g2-v1',
    constants: { A, B, M, Q },
    continuousPath,
    normalizedSquirclePath
  };
});
